import React from 'react';

import PropTypes from 'prop-types';
import cn from 'classnames';
import { sortableContainer, sortableElement } from 'react-sortable-hoc';
import { arrayMoveImmutable } from 'array-move';

import {
  Text,
  Image,
  uploadFile,
  toastError,
  uploadPhotos,
  toastWarning,
  uploadPhoto,
  Button,
} from 'modules/common';

import DropZone, { fileCondition } from './DropZone';
import * as c from './constants';

const SortableContainer = sortableContainer(({ children, ...rest }) => (
  <ul {...rest}>{children}</ul>
));

const SortableItem = sortableElement(({ children, ...rest }) => (
  <li
    {...rest}
    style={{
      listStyle: 'none',
    }}
  >
    {children}
  </li>
));

function Dropzone({
  label,
  name,
  id,
  required,
  isOptional,
  labelClassName,
  value,
  isMultiple,
  onChange,
  onSetFieldValue,
  imageProps,
  error,
  errorClassName,
  helperText,
  disabled,
  type,
  allowedExtensions,
  onError,
  description,
  dataClassName,
}) {
  const hasValue = isMultiple
    ? Array.isArray(value) && value?.length > 0
    : value?.length > 0 && value;
  const [isAdding, setIsAdding] = React.useState(false);

  const handleOnChange = (v) => {
    if (typeof onSetFieldValue === 'function') {
      onSetFieldValue(name, v);
      return;
    }
    if (typeof onChange === 'function') {
      onChange((prev) => ({
        ...prev,
        [name]: v,
      }));
    }
  };

  const handleOnError = () => {
    if (onError) {
      onError();
      return;
    }

    toastWarning(`Please upload ${allowedExtensions?.join(', ')} file only.`);
  };

  const handleOnAddFile = async () => {
    try {
      const $v = await uploadFile({
        transform: (x) => x,
      });

      fileCondition({
        allowedExtensions,
        file: $v?.sourceInfo?.file,
        cb: (v) => handleOnChange([v]),
        onError: handleOnError,
      });
    } catch (err) {
      toastError('Unable to upload file');
    }
  };

  const handleOnAdd = async (v) => {
    if (isMultiple) {
      handleOnChange((value || []).concat(v()?.url));
      return;
    }

    handleOnChange(v()?.url);
  };

  const handleOnRemove = async (index, onClose) => {
    if (isMultiple) {
      if (typeof onClose === 'function') {
        onClose();

        setTimeout(() => {
          value?.splice(index, 1);
          handleOnChange(value);
        }, 150);

        return;
      }

      value?.splice(index, 1);
      handleOnChange(value);

      return;
    }

    if (typeof onClose === 'function') {
      onClose();

      setTimeout(() => {
        handleOnChange('');
      }, 150);

      return;
    }

    handleOnChange('');
  };

  const handleOnDrop = async (v, setIsUploading) => {
    try {
      setIsUploading(true);
      const $v = await uploadPhotos(v);
      setIsUploading(false);

      if (isMultiple) {
        handleOnChange((value || []).concat($v));
        return;
      }

      handleOnChange($v);
    } catch (err) {
      toastError('Unable to upload file');
    }
  };

  const handleOnAddEmpty = async () => {
    try {
      setIsAdding(true);
      const v = await uploadPhoto({
        onCatch: () => {
          setIsAdding(false);
        },
      });
      setIsAdding(false);

      if (isMultiple) {
        handleOnChange((value || []).concat(v));
        return;
      }

      handleOnChange(v);
    } catch (err) {
      toastError('Unable to upload file');
    }
  };

  return (
    <div className="w-full relative">
      {disabled && (
        <div className="absolute top-0 left-0 h-full w-full bg-disabled bg-opacity-70 rounded z-20 cursor-not-allowed" />
      )}

      <DropZone
        onDrop={handleOnDrop}
        allowedExtensions={allowedExtensions}
        onError={handleOnError}
      >
        <div>
          {label && (
            <label
              className={cn('ds-form-label flex items-center', labelClassName)}
              htmlFor={id ?? name}
            >
              <div className="flex-1">
                {label}{' '}
                {required ? (
                  <span className="text-utility-danger-base">*</span>
                ) : (
                  ''
                )}
              </div>
              {isOptional && <div className="font-normal">Optional</div>}
            </label>
          )}
        </div>

        <div>
          {type === 'file' && value?.length > 0 ? (
            <div className="border-2 border-dashed border-gray-200 rounded p-8 flex flex-col text-center">
              <div className="m-auto space-y-3 flex flex-col items-center">
                {c.fileIcon}
                <div className="font-semibold">{value?.[0]?.name}</div>
                <div>
                  <Button
                    size="sm"
                    label="Replace File"
                    onClick={handleOnAddFile}
                  />
                </div>
              </div>
            </div>
          ) : (
            <>
              {!hasValue && !isAdding ? (
                <Empty
                  onAdd={handleOnAddEmpty}
                  error={error}
                  description={description}
                />
              ) : (
                <>
                  {isMultiple ? (
                    <Multiple
                      value={value}
                      onAdd={handleOnAdd}
                      imageProps={imageProps}
                      error={error}
                      onRemove={handleOnRemove}
                      dataClassName={dataClassName}
                    />
                  ) : (
                    <div
                      className={cn(
                        'flex p-ds-16 border rounded',
                        error
                          ? 'bg-utility-danger-muted border-utility-danger-base'
                          : 'border-default bg-primitive-white'
                      )}
                    >
                      <Image
                        src={value}
                        height="198px"
                        width="100%"
                        {...imageProps}
                        onRemove={handleOnRemove}
                        isLoading={isAdding}
                        setIsAdding={setIsAdding}
                      />
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>

        {(Boolean(error) || Boolean(helperText)) && (
          <Text
            type="caption"
            className={cn(
              'mt-ds-6',
              {
                'ds-form-helper': !error,
                'ds-form-helper-error': error,
                'ds-form-helper-disabled': disabled,
              },
              errorClassName
            )}
          >
            <span>{error || helperText || ''}</span>
            <span className="form-error-badge" />
          </Text>
        )}
      </DropZone>
    </div>
  );
}

Dropzone.defaultProps = {
  label: '',
  id: '',
  labelClassName: '',
  required: false,
  isOptional: false,
  isMultiple: false,
  onSetFieldValue: false,
  onChange: false,
  helperText: '',
  errorClassName: '',
  dataClassName: '',
  value: '',
  imageProps: {},
  error: false,
  disabled: false,
  type: 'image',
  allowedExtensions: ['csv', 'image'],
  onError: false,
  description: 'Describe here what to upload',
};

Dropzone.propTypes = {
  label: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.element,
    PropTypes.bool,
  ]),
  onError: PropTypes.oneOfType([
    PropTypes.instanceOf(Function),
    PropTypes.bool,
  ]),
  description: PropTypes.string,
  type: PropTypes.oneOf(['image', 'file']),
  allowedExtensions: PropTypes.instanceOf(Array),
  error: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.element,
    PropTypes.instanceOf(Object),
    PropTypes.bool,
  ]),
  disabled: PropTypes.bool,
  name: PropTypes.string.isRequired,
  helperText: PropTypes.string,
  dataClassName: PropTypes.string,
  isMultiple: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Array)]),
  imageProps: PropTypes.instanceOf(Object),
  labelClassName: PropTypes.string,
  id: PropTypes.string,
  required: PropTypes.bool,
  errorClassName: PropTypes.string,
  isOptional: PropTypes.bool,
  onSetFieldValue: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.instanceOf(Object),
  ]),
  onChange: PropTypes.oneOfType([PropTypes.bool, PropTypes.instanceOf(Object)]),
};

export default Dropzone;

const Empty = ({ onAdd, error, description }) => (
  <div
    className={cn(
      'flex p-ds-16 border rounded',
      error
        ? 'bg-utility-danger-muted border-utility-danger-base'
        : 'border-default bg-primitive-white'
    )}
  >
    <div
      className="m-auto flex flex-col items-center text-center gap-ds-4 xs:p-0 p-ds-16 hover:bg-light w-full cursor-pointer rounded"
      onClick={onAdd}
      role="presentation"
    >
      <div>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            d="M8 17C5.23858 17 3 14.7614 3 12C3 9.55154 4.75992 7.51413 7.08376 7.08376C7.51412 4.75992 9.55154 3 12 3C14.4485 3 16.4859 4.75992 16.9162 7.08376C19.2401 7.51413 21 9.55154 21 12C21 14.7614 18.7614 17 16 17M9 12L12 9M12 9L15 12M12 9V21"
            stroke="#333333"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>

      <div className="text-body space-y-ds-4">
        <div className="flex items-center flex-wrap justify-center gap-ds-4">
          <Text weight="semibold" color="brand-primary-base">
            Upload a file
          </Text>{' '}
          <Text weight="semibold">or drag and drop</Text>
        </div>

        <div>
          <Text type="caption" color="muted">
            {description}
          </Text>
        </div>
      </div>
    </div>
  </div>
);

Empty.defaultProps = {
  onAdd: () => {},
  error: false,
  description: '',
};

Empty.propTypes = {
  onAdd: PropTypes.instanceOf(Function),
  error: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.element,
    PropTypes.instanceOf(Object),
    PropTypes.bool,
  ]),
  description: PropTypes.string,
};

const Multiple = ({
  value,
  onAdd,
  imageProps,
  error,
  onRemove,
  dataClassName,
}) => {
  const [form, setForm] = React.useState(value);

  const handleSortEnd = ({ oldIndex, newIndex }) => {
    setForm((prevState) => arrayMoveImmutable(prevState, oldIndex, newIndex));
  };

  const dispatchPhotoGalleryProps = ({ index } = {}) => ({
    options: form?.map((subItem) => ({
      url: subItem,
    })),
    selectedIndex: index,
    isSolo: false,
    withBar: true,
    barProps: {
      withViewGallery: true,
      onDelete: (selectedIndex, onClose) => {
        onRemove(selectedIndex, onClose);
      },
    },
  });

  const renderList = () =>
    form?.map((item, key) => (
      <SortableItem
        // eslint-disable-next-line react/no-array-index-key
        key={key}
        index={key}
        className={cn('z-[1]', {
          'col-span-2 row-span-2': key === 0,
        })}
      >
        {key === 0 ? (
          <Image
            src={item}
            size="100%"
            withDrag
            {...imageProps}
            containerClassName="aspect-w-1 aspect-h-1"
            onRemove={() => onRemove(key)}
            photoGalleryProps={dispatchPhotoGalleryProps({ index: key })}
          />
        ) : (
          <Image
            src={item}
            size="100%"
            withDrag
            {...imageProps}
            containerClassName="aspect-w-1 aspect-h-1"
            onRemove={() => onRemove(key)}
            photoGalleryProps={dispatchPhotoGalleryProps({ index: key })}
          />
        )}
      </SortableItem>
    ));

  React.useEffect(() => {
    setForm(value);
  }, [value]);

  return (
    <div
      className={cn(
        'p-ds-16 border rounded',
        error
          ? 'bg-utility-danger-muted border-utility-danger-base'
          : 'border-default bg-primitive-white'
      )}
    >
      <SortableContainer
        className={cn(
          'grid xs:grid-cols-3 grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-ds-8',
          dataClassName
        )}
        onSortEnd={handleSortEnd}
        useDragHandle
        axis="xy"
      >
        {renderList()}

        <li
          style={{
            listStyle: 'none',
          }}
        >
          <Image
            withAdd
            onChange={onAdd}
            name="url"
            size="100%"
            containerClassName="aspect-w-1 aspect-h-1"
            src=""
            {...imageProps}
          />
        </li>
      </SortableContainer>
    </div>
  );
};

Multiple.defaultProps = {
  value: [],
  imageProps: {},
  onAdd: () => {},
  error: false,
  onRemove: () => {},
  dataClassName: '',
};

Multiple.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Array)]),
  imageProps: PropTypes.instanceOf(Object),
  onAdd: PropTypes.instanceOf(Function),
  onRemove: PropTypes.instanceOf(Function),
  dataClassName: PropTypes.string,
  error: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.element,
    PropTypes.instanceOf(Object),
    PropTypes.bool,
  ]),
};
